import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  {
    icon: Shield,
    title: "SEO Audits",
    description:
      "Review of your current website structure, technical issues, and search visibility gaps.",
  },
  {
    icon: TrendingUp,
    title: "Keyword Research",
    description:
      "Focused keyword planning based on service intent, relevance, and search opportunity.",
  },
  {
    icon: Layers,
    title: "On-Page SEO",
    description:
      "Optimization of headings, page structure, titles, descriptions, content, and internal linking.",
  },
  {
    icon: Zap,
    title: "Technical SEO",
    description:
      "Improvements to site health, crawlability, speed, indexing, and overall structure.",
  },
  {
    icon: Smartphone,
    title: "Local SEO Support",
    description:
      "SEO improvements that help service businesses appear more clearly in local search results.",
  },
  {
    icon: Code,
    title: "SEO Content Direction",
    description:
      "Guidance on service-page content, supporting content, and page-level keyword targeting.",
  },
];

const offerings = [
  {
    title: "Website SEO Foundations",
    description:
      "SEO improvements for websites that need stronger structure, page clarity, and search readiness.",
  },
  {
    title: "Service Page Optimization",
    description:
      "Page-level improvements that help core service pages target the right searches more clearly.",
  },
  {
    title: "Technical SEO Improvements",
    description:
      "Fixes and improvements that support indexing, speed, usability, and site health.",
  },
  {
    title: "Local Search Visibility",
    description:
      "SEO support for businesses that want to improve how they appear in local and regional searches.",
  },
];

const deliverables = [
  "SEO audit and findings summary",
  "Keyword direction for priority pages",
  "On-page optimization recommendations",
  "Technical SEO improvement list",
  "Internal linking suggestions",
  "Metadata guidance for key pages",
  "Content direction where needed",
  "Clear priorities for next steps",
];

const processSteps = [
  {
    step: "01",
    title: "Audit",
    description:
      "We review your website, structure, current visibility, and the main issues holding search performance back.",
  },
  {
    step: "02",
    title: "Research",
    description:
      "We identify relevant keywords, user intent, page opportunities, and supporting search themes.",
  },
  {
    step: "03",
    title: "Prioritize",
    description:
      "We define which pages, issues, and improvements matter most based on business goals.",
  },
  {
    step: "04",
    title: "Optimize",
    description:
      "We improve structure, on-page elements, internal links, and technical foundations.",
  },
  {
    step: "05",
    title: "Strengthen Content",
    description:
      "Where needed, we improve page copy and content direction to better match search intent.",
  },
  {
    step: "06",
    title: "Monitor & Improve",
    description:
      "SEO is not one-time work. We continue refining based on performance and evolving priorities.",
  },
];

const faqs = [
  {
    question: "What is included in your SEO service?",
    answer: (
      <>
        Our SEO service may include audits, keyword research, on-page SEO,
        technical improvements, internal linking suggestions, and{" "}
        <Link
          to="/services/social-media-marketing"
          className="text-primary hover:underline"
        >
          content direction
        </Link>{" "}
        depending on what your website needs.
      </>
    ),
    schemaAnswer:
      "Our SEO service may include audits, keyword research, on-page SEO, technical improvements, internal linking suggestions, and content direction depending on what your website needs.",
  },
  {
    question: "How long does SEO take to show results?",
    answer: (
      <>
        SEO usually takes time to build momentum. Timelines vary depending on
        competition, website condition, and the scope of work, but it is a
        long-term growth channel.
      </>
    ),
    schemaAnswer:
      "SEO usually takes time to build momentum. Timelines vary depending on competition, website condition, and the scope of work, but it is a long-term growth channel.",
  },
  {
    question: "Do you only do local SEO?",
    answer: (
      <>
        No. We can support local search visibility as well as broader
        service-page and website SEO depending on your goals.
      </>
    ),
    schemaAnswer:
      "No. We can support local search visibility as well as broader service-page and website SEO depending on your goals.",
  },
  {
    question: "Can you improve the SEO of an existing website?",
    answer: (
      <>
        Yes. We can improve site structure, metadata, internal linking, content
        alignment, and{" "}
        <Link
          to="/services/maintenance-support"
          className="text-primary hover:underline"
        >
          technical SEO
        </Link>{" "}
        on an{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          existing website
        </Link>
        .
      </>
    ),
    schemaAnswer:
      "Yes. We can improve site structure, metadata, internal linking, content alignment, and technical SEO on an existing website.",
  },
  {
    question: "Do you write SEO content too?",
    answer: (
      <>
        We provide content direction and page-level recommendations. Where
        needed, we can also support{" "}
        <Link
          to="/services/social-media-marketing"
          className="text-primary hover:underline"
        >
          content improvements
        </Link>{" "}
        that better match search intent.
      </>
    ),
    schemaAnswer:
      "We provide content direction and page-level recommendations. Where needed, we can also support content improvements that better match search intent.",
  },
  {
    question: "Does SEO work better with a strong website?",
    answer: (
      <>
        Yes. SEO works best when the{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          website structure, speed, usability
        </Link>
        , and content all support the same goal.
      </>
    ),
    schemaAnswer:
      "Yes. SEO works best when the website structure, speed, usability, and content all support the same goal.",
  },
];

const whyChooseUs = [
  {
    title: "Strategy Before Tactics",
    description:
      "We define what needs to improve before making changes, so the SEO work has direction.",
  },
  {
    title: "Built Around Real Search Intent",
    description:
      "We focus on the keywords, pages, and structure that match how your ideal client actually searches.",
  },
  {
    title: "Technical + Content Thinking",
    description:
      "SEO works best when site performance, structure, and content all support the same goal.",
  },
  {
    title: "Practical Growth Focus",
    description:
      "The goal is not just more traffic. The goal is better visibility that supports real business growth.",
  },
];

export default function WebsiteDevelopmentPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      <SEO
        title="Best Search Engine Optimization Service | Square Trigger"
        description="Get Affordable SEO services in Canada that help improve your Google ranking and drive more traffic and revenue. Start ranking higher today."
        ogTitle="Best Search Engine Optimization Service | Square Trigger"
        ogDescription="Get Affordable SEO services in Canada that help improve your Google ranking and drive more traffic and revenue. Start ranking higher today."
        keywords={SEO_KEYWORDS.seoServices}
      />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
        <div className="absolute inset-0">
          <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 bg-primary/20 text-primary-foreground border-none">
                <Globe className="h-4 w-4 mr-2" />
                Our Services
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
            >
              Search Engine Optimization Services That Help the Right Clients{" "}
              <span className="gradient-text">Find You</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg sm:text-xl text-background/70 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              We provide SEO services that improve visibility, strengthen search
              performance, and help your business get found by people already
              looking for what you offer.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center"
            >
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/contact">View Our Work</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="pt-12 sm:pt-14 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              Get Found When People Are Already{" "}
              <span className="gradient-text">Searching</span>
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
              SEO helps your business appear when potential clients are actively
              looking for your services. It is not just about rankings. It is
              about showing up at the right time, for the right search, with the
              right page.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/60 backdrop-blur rounded-2xl border border-border shadow-soft p-6 sm:p-8"
            >
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
                A strong SEO strategy improves visibility, supports trust, and
                helps bring in traffic that is more likely to convert.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                {[
                  {
                    title: "Better visibility",
                    desc: "Appear more often in search results for the services your business actually offers.",
                  },
                  {
                    title: "Qualified traffic",
                    desc: "SEO helps bring in people who are already searching with intent.",
                  },
                  {
                    title: "Long-term value",
                    desc: "Unlike short-term campaigns, SEO builds stronger visibility over time.",
                  },
                  {
                    title: "Stronger trust",
                    desc: "People trust businesses they can find clearly and consistently online.",
                  },
                  {
                    title: "Better digital foundation",
                    desc: "SEO works best when your website structure, content, and performance all support it.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-center hover:shadow-medium transition-shadow"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.title === "Better digital foundation" ? (
                        <>
                          SEO works best when your{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            website structure
                          </Link>
                          ,{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>
                          , and{" "}
                          <Link
                            to="/services/maintenance-support"
                            className="text-primary hover:underline"
                          >
                            performance
                          </Link>{" "}
                          all support it.
                        </>
                      ) : (
                        item.desc
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="pt-12 sm:pt-14 lg:pt-14 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
              Why Choose Square Trigger for SEO Services
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
              We do not treat SEO like a checklist. We look at how your website
              is built, how your pages are structured, what your audience is
              searching for, and what is stopping the business from being found
              more effectively.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="p-6 bg-card rounded-2xl border border-border shadow-soft hover:shadow-medium transition-shadow text-center sm:text-left flex flex-col items-center sm:items-start"
              >
                <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center mb-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                </div>

                <h3 className="font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.title === "Technical + Content Thinking" ? (
                    <>
                      SEO works best when{" "}
                      <Link
                        to="/services/maintenance-support"
                        className="text-primary hover:underline"
                      >
                        site performance
                      </Link>
                      ,{" "}
                      <Link
                        to="/services/website-development"
                        className="text-primary hover:underline"
                      >
                        structure
                      </Link>
                      , and{" "}
                      <Link
                        to="/services/social-media-marketing"
                        className="text-primary hover:underline"
                      >
                        content
                      </Link>{" "}
                      all support the same goal.
                    </>
                  ) : (
                    item.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-14"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              What Our SEO Services <span className="gradient-text">Include</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
                    <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                      {capability.title}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {capability.title === "SEO Audits" ? (
                        <>
                          Review of your current website{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            structure
                          </Link>
                          , technical issues, and search visibility gaps.
                        </>
                      ) : capability.title === "On-Page SEO" ? (
                        <>
                          Optimization of headings,{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            page structure
                          </Link>
                          , titles, descriptions,{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>
                          , and internal linking.
                        </>
                      ) : capability.title === "Technical SEO" ? (
                        <>
                          Improvements to{" "}
                          <Link
                            to="/services/maintenance-support"
                            className="text-primary hover:underline"
                          >
                            site health, crawlability, speed
                          </Link>
                          , indexing, and overall structure.
                        </>
                      ) : capability.title === "SEO Content Direction" ? (
                        <>
                          Guidance on service-page{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>
                          , supporting content, and page-level keyword targeting.
                        </>
                      ) : (
                        capability.description
                      )}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-secondary uppercase tracking-wider">
              Solutions
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold mt-3">
              SEO Support Built Around <span className="gradient-text">Business Goals</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="bg-background/5 rounded-2xl p-6 sm:p-8 border border-background/10 hover:border-primary/30 transition-colors text-center sm:text-left flex flex-col items-center sm:items-start"
              >
                <h3 className="text-xl sm:text-2xl font-display font-bold mb-3">
                  {offering.title === "Website SEO Foundations" ? (
                    <Link
                      to="/services/website-development"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : offering.title === "Technical SEO Improvements" ? (
                    <Link
                      to="/services/maintenance-support"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : (
                    offering.title
                  )}
                </h3>

                <p className="text-background/70 leading-relaxed">
                  {offering.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                Deliverables
              </span>

              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3 mb-5">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Our SEO service is built around what your website actually needs
                to improve visibility and search performance.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="bg-card rounded-2xl p-6 sm:p-8 border border-border shadow-soft text-center sm:text-left"
            >
              <ul className="inline-grid sm:grid space-y-4 text-left">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3 w-fit"
                  >
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-[2px]" />
                    <span className="text-sm sm:text-base text-foreground/90 leading-relaxed">
                      {item === "Metadata guidance for key pages" ? (
                        <>
                          Metadata guidance for{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            key pages
                          </Link>
                        </>
                      ) : item === "Content direction where needed" ? (
                        <>
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            Content direction
                          </Link>{" "}
                          where needed
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="pt-12 sm:pt-14 lg:pt-12 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              How We <span className="gradient-text">Approach SEO</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="p-6 h-full border-border hover:border-primary/30 transition-colors card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
                  <div className="w-full mb-4 flex justify-center">
                    <div className="text-3xl font-display font-bold gradient-text text-center">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {step.title === "Optimize" ? (
                      <>
                        We improve structure, on-page elements, internal links,
                        and{" "}
                        <Link
                          to="/services/maintenance-support"
                          className="text-primary hover:underline"
                        >
                          technical foundations
                        </Link>
                        .
                      </>
                    ) : step.title === "Strengthen Content" ? (
                      <>
                        Where needed, we improve page copy and{" "}
                        <Link
                          to="/services/social-media-marketing"
                          className="text-primary hover:underline"
                        >
                          content direction
                        </Link>{" "}
                        to better match search intent.
                      </>
                    ) : (
                      step.description
                    )}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="py-16 sm:py-20 gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4 sm:mb-6">
              Need SEO That Supports Real Business Growth?
            </h2>

            <p className="text-base sm:text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Let’s look at what is holding your website back in search and what
              needs to improve first.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="premium"
                size="lg"
                asChild
                className="bg-background text-foreground hover:bg-background/90 w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              SEO Services –{" "}
              <span className="gradient-text">Frequently Asked Questions</span>
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                className="relative z-10 bg-card rounded-xl border border-border overflow-hidden"
              >
                <button
                  type="button"
                  onPointerUp={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors pointer-events-auto touch-manipulation"
                >
                  <span className="font-semibold text-foreground pr-6">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.schemaAnswer,
                },
              })),
            })}
          </script>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 sm:mb-6">
              Ready to Improve Your Search Visibility?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
              Let’s identify what your website needs to rank more clearly,
              attract better traffic, and support stronger growth.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="outline"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center"
                >
                  Contact Us
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}